/**
 * Updates headings marked with data-size-guide-dynamic-title (e.g. rich-text-plus).
 * @param {string} title
 */
function syncSizeGuideDynamicTitle(title) {
  const text = String(title || '').trim();
  if (!text) return;

  document.querySelectorAll('[data-size-guide-dynamic-title]').forEach((el) => {
    el.textContent = text.split(' - ')[0];
  });
}

/**
 * @param {string} category
 * @param {string} [title]
 * @param {'service-page-tabs' | 'custom-size-carousel'} [source]
 */
function dispatchSizeGuideCategoryChange(category, title, source) {
  document.dispatchEvent(
    new CustomEvent('size-guide:category-change', {
      detail: {
        category: String(category || '').trim().toLowerCase(),
        title: String(title || '').trim(),
        source: source || '',
      },
    })
  );
}

/**
 * @returns {HTMLElement | null}
 */
function getActiveCarouselSlide() {
  const carousel = document.querySelector('custom-size-carousel');
  if (!carousel) return null;

  return (
    carousel.querySelector('[data-category].is-active') ||
    carousel.querySelector('[data-category][aria-pressed="true"]') ||
    carousel.querySelector('[data-category]')
  );
}

/**
 * Syncs table + dynamic title from the carousel's active card.
 */
function syncFromActiveCarouselSlide() {
  const slide = getActiveCarouselSlide();
  if (!slide) return;

  const category = slide.dataset.category || '';
  const title = slide.dataset.title || '';

  dispatchSizeGuideCategoryChange(category, title, 'custom-size-carousel');
  syncSizeGuideDynamicTitle(title);
}

/**
 * Runs after other custom elements (custom-size-table, rich-text) have connected.
 */
function scheduleInitialCarouselSync() {
  queueMicrotask(() => {
    requestAnimationFrame(syncFromActiveCarouselSlide);
  });
}

class CustomSizeCarousel extends HTMLElement {
  constructor() {
    super();

    this.handleSlideClick = this.handleSlideClick.bind(this);
    this.handleSlideKeydown = this.handleSlideKeydown.bind(this);
    this.handleCategoryChange = this.handleCategoryChange.bind(this);
  }

  connectedCallback() {
    if (this.initialized) {
      return;
    }

    this.slides = [...this.querySelectorAll('[data-category]')];

    if (!this.slides.length) {
      return;
    }

    this.addEventListener('click', this.handleSlideClick);
    this.addEventListener('keydown', this.handleSlideKeydown);
    document.addEventListener('size-guide:category-change', this.handleCategoryChange);

    this.initialized = true;
    scheduleInitialCarouselSync();
  }

  disconnectedCallback() {
    if (!this.initialized) {
      return;
    }

    this.removeEventListener('click', this.handleSlideClick);
    this.removeEventListener('keydown', this.handleSlideKeydown);
    document.removeEventListener('size-guide:category-change', this.handleCategoryChange);

    this.initialized = false;
  }

  /**
   * @param {CustomEvent} event
   */
  handleCategoryChange(event) {
    const { category, source } = event.detail || {};
    if (source === 'custom-size-carousel') {
      return;
    }

    const normalised = String(category || '').trim().toLowerCase();
    if (!normalised) {
      return;
    }

    const slide = this.slides.find(
      (s) => (s.dataset.category || '').trim().toLowerCase() === normalised
    );

    if (!slide) {
      return;
    }

    this.selectSlide(slide, { dispatch: false });
  }

  /**
   * @param {MouseEvent} event
   */
  handleSlideClick(event) {
    const slide = event.target.closest('[data-category]');

    if (!slide) {
      return;
    }

    this.selectSlide(slide);
  }

  /**
   * @param {KeyboardEvent} event
   */
  handleSlideKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    const slide = event.target.closest('[data-category]');

    if (!slide) {
      return;
    }

    event.preventDefault();
    this.selectSlide(slide);
  }

  /**
   * @param {HTMLElement} slide
   * @param {{ dispatch?: boolean }} [options]
   */
  selectSlide(slide, options = {}) {
    const { dispatch = true } = options;
    this.slides.forEach((s) => {
      s.classList.remove('is-active');
      s.setAttribute('aria-pressed', 'false');
    });

    slide.classList.add('is-active');
    slide.setAttribute('aria-pressed', 'true');

    const slideIndex = this.slides.indexOf(slide);
    const swiperSlider = this.querySelector('custom-swiper-slider');

    if (swiperSlider?.swiper && slideIndex !== -1) {
      swiperSlider.swiper.slideTo(slideIndex);
    }

    if (dispatch) {
      dispatchSizeGuideCategoryChange(
        slide.dataset.category,
        slide.dataset.title,
        'custom-size-carousel'
      );
      syncSizeGuideDynamicTitle(slide.dataset.title);
    }
  }
}

if (!customElements.get('custom-size-carousel')) {
  customElements.define('custom-size-carousel', CustomSizeCarousel);
}

document.addEventListener('size-guide:category-change', (event) => {
  const { title, source } = event.detail || {};
  if (source !== 'custom-size-carousel') {
    syncSizeGuideDynamicTitle(title);
  }
});

document.addEventListener('DOMContentLoaded', () => {
  if (document.querySelector('custom-size-carousel')) {
    syncFromActiveCarouselSlide();
  }
});
